import { Link } from "@inertiajs/react"
import { Download, ExternalLink, ImageOff } from "lucide-react"
import { formatDay } from "@/lib/dates"
import type { AdminMockupSummary } from "@/types/inertia"

// A customer's label design as admin sees it (M7): the stored bottle
// composite with a download, never the studio (W1 ruling 3). Used on the
// dashboard and on an enquiry's page.
export function MockupCard({ mockup, showEnquiry = true }: { mockup: AdminMockupSummary; showEnquiry?: boolean }) {
  const owner =
    mockup.owner.kind === "account"
      ? mockup.owner.email
      : `Guest${mockup.owner.email ? ` · ${mockup.owner.email}` : " (no email yet)"}`

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-md border border-hairline bg-page">
      <div className="flex aspect-[3/4] items-center justify-center bg-surface">
        {mockup.preview_url ? (
          <img
            src={mockup.preview_url}
            alt={`Bottle preview: ${mockup.title}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="flex flex-col items-center gap-2 text-xs text-ink-muted">
            <ImageOff className="h-6 w-6" aria-hidden="true" />
            No preview yet
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <div className="text-sm font-medium text-ink-display">{mockup.title}</div>
        <div className="break-words text-xs text-ink-muted">{owner}</div>
        <div className="text-xs text-ink-muted">
          {mockup.source_path === "ai" ? "Painted from a description" : "Template or own artwork"} ·{" "}
          {formatDay(mockup.created_at)}
        </div>
        {mockup.ai_prompt && <div className="break-words text-xs text-ink-body">“{mockup.ai_prompt}”</div>}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 pt-1">
          {showEnquiry && mockup.enquiry_url && (
            <Link href={mockup.enquiry_url} className="inline-flex min-h-11 items-center text-sm">
              Enquiry #{mockup.enquiry_id}
            </Link>
          )}
          {mockup.preview_url && (
            <a
              href={mockup.preview_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center gap-1 text-sm"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Open<span className="sr-only"> {mockup.title}</span>
            </a>
          )}
          {mockup.download_url && (
            <a href={mockup.download_url} download className="inline-flex min-h-11 items-center gap-1 text-sm">
              <Download className="h-4 w-4" aria-hidden="true" />
              Download<span className="sr-only"> {mockup.title}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
